import { ArrowRight, CheckCircle, Envelope, Phone, WarningCircle } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { business, services } from '../content/site';
import { buildLeadPayload, makeFallbackEmail, submitLead } from '../lib/lead';
import { getPrimaryServices } from '../lib/serviceDisplay';

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  preferredContactMethod: 'phone',
  bestTimeToContact: '',
  serviceType: '',
  urgency: '',
  homeownerStatus: '',
  city: '',
  zip: '',
  addressLine1: '',
  message: '',
  consentToContact: false,
  companyWebsite: '',
};

export function ContactForm() {
  const primaryServices = getPrimaryServices(services);
  const [values, setValues] = useState(emptyForm);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const fallbackEmail = useMemo(() => makeFallbackEmail(values), [values]);

  const update = (event) => {
    const { name, type, checked, value } = event.target;
    setValues((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    if (status !== 'idle') setStatus('idle');
    if (error) setError('');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (status === 'submitting') return;
    setError('');

    if (!business.contactEndpoint) {
      setStatus('fallback');
      return;
    }

    setStatus('submitting');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      await submitLead(business.contactEndpoint, buildLeadPayload(values), controller.signal);
      setStatus('success');
      setValues(emptyForm);
    } catch (caught) {
      const message = caught.name === 'AbortError'
        ? 'The request took too long. Your details are still here so you can try again or contact us directly.'
        : caught.message || 'We could not send your request. Your details are still here.';
      setError(message);
      setStatus('error');
    } finally {
      window.clearTimeout(timeout);
    }
  };

  if (status === 'success') {
    return (
      <div className="form-result form-result--success" role="status">
        <CheckCircle aria-hidden="true" size={38} weight="fill" />
        <h2>Your request was received.</h2>
        <p>Thank you. Your project details are on file, and Vic’s Bricks will follow up using the contact information you provided.</p>
        <button className="button button--secondary" type="button" onClick={() => setStatus('idle')}>Send another request</button>
      </div>
    );
  }

  return (
    <form className="contact-form" onSubmit={onSubmit} noValidate={false} aria-busy={status === 'submitting'}>
      <fieldset className="contact-form__fields" disabled={status === 'submitting'}>
      <div className="form-section">
        <div className="form-section__heading"><span>Contact</span><h2>How can we reach you?</h2></div>
        <div className="form-grid form-grid--two">
          <label><span>First name</span><input name="firstName" autoComplete="given-name" value={values.firstName} onChange={update} maxLength="100" required /></label>
          <label><span>Last name</span><input name="lastName" autoComplete="family-name" value={values.lastName} onChange={update} maxLength="100" required /></label>
          <label><span>Email</span><input type="email" name="email" autoComplete="email" value={values.email} onChange={update} maxLength="254" required /></label>
          <label><span>Phone</span><input type="tel" name="phone" autoComplete="tel" value={values.phone} onChange={update} maxLength="50" required /></label>
          <label><span>Preferred contact</span><select name="preferredContactMethod" value={values.preferredContactMethod} onChange={update}><option value="phone">Phone call</option><option value="text">Text message</option><option value="email">Email</option></select></label>
          <label><span>Best time to contact</span><input name="bestTimeToContact" value={values.bestTimeToContact} onChange={update} maxLength="255" placeholder="Example: weekday afternoons" /></label>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section__heading"><span>Project</span><h2>What does the property need?</h2></div>
        <div className="form-grid form-grid--two">
          <label><span>Service</span><select name="serviceType" value={values.serviceType} onChange={update} required><option value="">Choose a service</option>{primaryServices.map((service) => <option key={service.slug} value={service.name}>{service.name}</option>)}<option value="Not sure">Not sure yet</option></select></label>
          <label><span>Timing</span><select name="urgency" value={values.urgency} onChange={update}><option value="">Choose a timeframe</option><option value="as-soon-as-possible">As soon as possible</option><option value="this-month">Within a month</option><option value="one-to-three-months">1 to 3 months</option><option value="planning">Planning ahead</option></select></label>
          <label><span>City</span><input name="city" autoComplete="address-level2" value={values.city} onChange={update} maxLength="255" /></label>
          <label><span>ZIP code</span><input name="zip" inputMode="numeric" autoComplete="postal-code" value={values.zip} onChange={update} maxLength="25" /></label>
          <label className="form-grid__full"><span>Project address</span><input name="addressLine1" autoComplete="street-address" value={values.addressLine1} onChange={update} maxLength="255" /></label>
          <label><span>Property relationship</span><select name="homeownerStatus" value={values.homeownerStatus} onChange={update}><option value="">Choose one</option><option value="homeowner">Homeowner</option><option value="property-manager">Property manager</option><option value="builder">Builder or contractor</option><option value="buyer">Buying the property</option><option value="other">Other</option></select></label>
          <label className="form-grid__full"><span>Project details</span><textarea name="message" value={values.message} onChange={update} maxLength="5000" rows="7" required placeholder="Tell us what you see, what you want to change, and any access or drainage concerns." /><small>Photos can be shared during follow-up.</small></label>
        </div>
      </div>

      <label className="honeypot" aria-hidden="true">Company website<input name="companyWebsite" tabIndex="-1" autoComplete="off" value={values.companyWebsite} onChange={update} /></label>

      <label className="consent"><input type="checkbox" name="consentToContact" checked={values.consentToContact} onChange={update} required /><span>I agree that Vic’s Bricks may contact me about this project. See the <Link to="/privacy">privacy notice</Link>.</span></label>

      {status === 'fallback' && (
        <div className="form-result form-result--fallback" role="status">
          <WarningCircle aria-hidden="true" size={28} weight="fill" />
          <div><h3>Online delivery is not connected in this preview.</h3><p>Your details are still in the form. Use the prepared email or call Vic’s Bricks directly.</p><div><a className="button button--secondary" href={fallbackEmail}><Envelope aria-hidden="true" size={17} /> Open Email</a><a className="button button--secondary" href={business.phoneHref}><Phone aria-hidden="true" size={17} weight="fill" /> Call {business.phoneDisplay}</a></div></div>
        </div>
      )}

      {error && <div className="form-error" role="alert"><WarningCircle aria-hidden="true" size={22} weight="fill" /><span>{error} Call <a href={business.phoneHref}>{business.phoneDisplay}</a> or email <a href={business.emailHref}>{business.email}</a>.</span></div>}

      <button className="button button--primary form-submit" type="submit" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending project details' : 'Request My Estimate'}
        {status !== 'submitting' && <ArrowRight aria-hidden="true" size={18} weight="bold" />}
      </button>
      {status === 'submitting' && <span className="submit-progress" aria-hidden="true" />}
      </fieldset>
    </form>
  );
}
